import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from './useSocket';
import { useAuth } from './useAuth';
import { Order } from '../types';
import { ordersAPI } from '../services/api';

interface UseOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: string | null;
  refreshOrders: () => Promise<void>;
}

export const useOrders = (platform?: string): UseOrdersReturn => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { socket, isConnected } = useSocket();
  const { store } = useAuth();

  // Fetch orders from the CURRENT register period (since the last register close
  // in manual mode, else today's business-day boundary) rather than a fixed
  // last-80 window — so the cashier sees every order (incl. completed) of the
  // open register, not just the most recent 80.
  const refreshOrders = useCallback(async () => {
    if (!store) return;

    try {
      setLoading(true);
      setError(null);

      const params = platform
        ? { platform_name: platform, since_register: 1 }
        : { since_register: 1 };
      const response = await ordersAPI.getOrders(store.id, params);
      setOrders(response.orders || []);
    } catch (err: any) {
      console.error('Failed to fetch orders:', err);
      setError(err.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [store, platform]);

  // Initial load
  useEffect(() => {
    refreshOrders();
  }, [refreshOrders]);

  // RE-CONNECT catch-up: orders that arrived while the socket was down never
  // fired 'new-order', so without a refetch they simply don't exist for this
  // screen (and a platform order could expire unanswered). Track the previous
  // connection state so we only refetch on a true DOWN→UP transition, not on
  // the initial connect (the mount effect above already loaded).
  const wasDisconnected = useRef(false);
  useEffect(() => {
    if (!isConnected) {
      wasDisconnected.current = true;
      return;
    }
    if (wasDisconnected.current) {
      wasDisconnected.current = false;
      refreshOrders();
    }
  }, [isConnected, refreshOrders]);

  // WebSocket event handlers
  useEffect(() => {
    if (!socket || !isConnected) return;

    // Handle new orders
    const handleNewOrder = (order: Order) => {
      // Only add if it matches the current platform filter
      if (!platform || order.platform_name === platform) {
        setOrders(prevOrders => [order, ...prevOrders]);
      }
      // Καμία ειδοποίηση συστήματος εδώ: η παλιά in-page `new Notification` έφυγε 14/9/2026
      // (δούλευε μόνο με ανοιχτή σελίδα) και η εγγραφή Web Push από τις Ρυθμίσεις αφαιρέθηκε
      // 12/10/2026 (0 συσκευές σε 40 καταστήματα)· το backend pushService μένει αδρανές.
    };

    // Handle order updates
    const handleOrderUpdate = (updatedOrder: Order) => {
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === updatedOrder.id ? updatedOrder : order
        )
      );
    };

    // Handle order status changes
    const handleOrderStatusChange = (data: { orderId: number; status: string }) => {
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === data.orderId ? { ...order, status: data.status } : order
        )
      );
    };

    // Register event listeners
    socket.on('new-order', handleNewOrder);
    socket.on('order-updated', handleOrderUpdate);
    socket.on('order-status-changed', handleOrderStatusChange);

    // Cleanup
    return () => {
      socket.off('new-order', handleNewOrder);
      socket.off('order-updated', handleOrderUpdate);
      socket.off('order-status-changed', handleOrderStatusChange);
    };
  }, [socket, isConnected, platform]);

  return {
    orders,
    loading,
    error,
    refreshOrders
  };
};

